import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AUTH_ERROR_EVENT } from '../services/api';

interface AdminRouteProps {
  redirectPath?: string;
  signInPath?: string;
}

/**
 * Route component that only allows access to users with admin privileges
 * Redirects non-admin users to the specified redirectPath
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ 
  redirectPath = '/dashboard',
  signInPath = '/sign-in'
}) => {
  const { isAuthenticated, isLoading, user, signOut } = useAuth();
  const navigate = useNavigate();

  // Listen for authentication error events
  useEffect(() => {
    const handleAuthError = async (event: Event) => {
      // Sign out the user
      try {
        await signOut();
      } catch (error) {
        console.error('Error signing out after auth error:', error);
      }
      
      // Navigate to sign-in page with message
      navigate(signInPath, { 
        state: { 
          message: 'Your session has expired. Please sign in again.',
          from: window.location.pathname
        } 
      });
    };

    // Add event listener
    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);

    // Clean up
    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
    };
  }, [navigate, signInPath, signOut]);

  // Show loading indicator while auth state is being checked
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={signInPath} replace state={{ from: window.location.pathname }} />;
  }

  // Check if user has admin role or is in admin group
  const isAdmin = 
    user?.role === 'admin' || 
    user?.groups?.includes('admin');

  // Redirect non-admin users to the specified redirectPath
  if (!isAdmin) {
    return <Navigate to={redirectPath} replace />;
  }

  // Render the child routes for admin users
  return <Outlet />;
};

export default AdminRoute;